/**
 * PushNotificationsToggle — drop-in card that lets the user enable / disable
 * web push for the current device. Uses `usePushNotifications` under the
 * hood and renders contextual copy depending on role.
 *
 * Props:
 *   role: 'restaurant' | 'customer'
 *   token: auth JWT for the audience
 *   variant?: 'card' | 'inline' (defaults to 'card')
 */
import React from 'react';
import { Bell, BellOff, BellRing, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import usePushNotifications from '../hooks/usePushNotifications';

const COPY = {
  restaurant: {
    title: 'Push notifications',
    desc: 'Get instant alerts on this device when a new order comes in — even when the dashboard is closed.',
    enabledDesc: 'You\'ll hear from us the moment a new order lands.',
  },
  customer: {
    title: 'Push notifications',
    desc: 'Get alerts when your orders are confirmed, prepared and delivered. Works on your phone home screen.',
    enabledDesc: 'You\'ll be notified as your order moves through the kitchen.',
  },
};

export default function PushNotificationsToggle({ role, token, variant = 'card' }) {
  const { supported, permission, subscribed, loading, enable, disable, sendTest } =
    usePushNotifications({ role, token });

  const copy = COPY[role] || COPY.restaurant;

  const handleEnable = async () => {
    const r = await enable();
    if (r.ok) {
      toast.success('Push notifications enabled');
      // Send a test push so the user immediately sees it works.
      setTimeout(() => sendTest(), 400);
    } else if (r.reason === 'denied') {
      toast.error('Permission denied. Update your browser settings to allow notifications.');
    } else {
      toast.error(`Could not enable: ${r.reason || 'unknown error'}`);
    }
  };

  const handleDisable = async () => {
    const r = await disable();
    if (r.ok) toast.success('Push notifications disabled');
  };

  const handleTest = async () => {
    const r = await sendTest();
    if (r.ok && r.sent > 0) toast.success('Test notification sent');
    else if (r.ok) toast.message('No active subscriptions — try toggling Off then On.');
    else toast.error(`Failed: ${r.reason || 'unknown'}`);
  };

  if (!supported) {
    if (variant === 'inline') return null;
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="push-not-supported">
        <strong>Push notifications aren\u2019t supported on this browser.</strong>
        <p className="mt-1 text-amber-800/80">Open NoFee in Chrome / Edge / Firefox / Safari 16.4+ on a phone or desktop to enable alerts.</p>
      </div>
    );
  }

  // Permission was explicitly DENIED — the user has to flip it in browser settings.
  const blocked = permission === 'denied';

  return (
    <div
      className={
        variant === 'card'
          ? 'rounded-xl border border-slate-200 bg-white p-5 shadow-sm'
          : 'flex items-center gap-3'
      }
      data-testid="push-toggle"
    >
      {variant === 'card' && (
        <div className="flex items-start gap-3">
          <div className={`shrink-0 rounded-full p-2 ${subscribed ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-600'}`}>
            {subscribed ? <BellRing className="h-5 w-5" /> : <Bell className="h-5 w-5" />}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-slate-900" data-testid="push-toggle-title">{copy.title}</h3>
              {subscribed && (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
                  <CheckCircle2 className="h-3 w-3" /> On
                </span>
              )}
            </div>
            <p className="mt-1 text-sm text-slate-600" data-testid="push-toggle-desc">
              {subscribed ? copy.enabledDesc : copy.desc}
            </p>

            {blocked && (
              <p className="mt-2 text-xs text-rose-700" data-testid="push-toggle-blocked">
                Notifications are blocked in your browser. Click the lock icon next to the address bar and allow them.
              </p>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
              {subscribed ? (
                <>
                  <button
                    type="button"
                    onClick={handleDisable}
                    disabled={loading}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3.5 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
                    data-testid="push-toggle-disable"
                  >
                    <BellOff className="h-4 w-4" /> Turn off
                  </button>
                  <button
                    type="button"
                    onClick={handleTest}
                    disabled={loading}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                    data-testid="push-toggle-test"
                  >
                    Send test notification
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={handleEnable}
                  disabled={loading || blocked}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-gold-500 px-4 py-2 text-sm font-semibold text-black hover:bg-gold-400 disabled:opacity-50"
                  data-testid="push-toggle-enable"
                >
                  <Bell className="h-4 w-4" /> {loading ? 'Enabling...' : 'Enable notifications'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {variant === 'inline' && (
        subscribed ? (
          <button
            type="button"
            onClick={handleDisable}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100"
            data-testid="push-toggle-inline-on"
          >
            <BellRing className="h-3.5 w-3.5" /> Push on
          </button>
        ) : (
          <button
            type="button"
            onClick={handleEnable}
            disabled={loading || blocked}
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            data-testid="push-toggle-inline-off"
          >
            <Bell className="h-3.5 w-3.5" /> Enable push
          </button>
        )
      )}
    </div>
  );
}
